'use client'

import type { SerializedEditorState } from '@payloadcms/richtext-lexical/lexical'
import type { JSXConvertersFunction } from '@payloadcms/richtext-lexical/react'
import * as React from 'react'
import { defaultJSXConverters, RichText as PayloadRichText } from '@payloadcms/richtext-lexical/react'
import { Download } from 'lucide-react'
import Zoom from 'react-medium-image-zoom'

import cn from '@config/tailwind/cn'

import type { MediaType } from './types'
import { CalloutBox } from '../../components/callout-box'
import { typographyVariants } from '../typography'
import { AlertBlock } from './alert-block'
import { BlogShowcaseBlock } from './blog-showcase-block'
import { BookShowcaseBlock } from './book-showcase-block'
import { CanvaIframeBlock } from './canva-iframe-block'
import { CtaBlock } from './cta-block'
import { FeatureGridBlock } from './feature-grid-block'
import { GalleryBlock } from './gallery-block'
import { HeaderBlock } from './header-block'
import { QuoteBlock } from './quote-block'
import { YoutubeIframeBlock } from './youtube-iframe-block'

/* =============================================================================
   Custom Block Field Types
   ============================================================================= */

interface AlertBlockFields {
  blockType: 'alert'
  variant: 'default' | 'destructive'
  title: string
  description?: string
  icon?:
    | ''
    | 'AlertCircle'
    | 'AlertTriangle'
    | 'Info'
    | 'CheckCircle'
    | 'XCircle'
    | 'AlertOctagon'
    | 'Bell'
    | 'BellRing'
    | 'Shield'
    | 'ShieldAlert'
    | 'Lightbulb'
    | 'Megaphone'
}

interface CalloutBoxBlockFields {
  blockType: 'callout-box'
  heading: string
  ctaText?: string
  link?: string
}

interface QuoteBlockFields {
  blockType: 'quote'
  quote: string
}

interface GalleryBlockFields {
  blockType: 'gallery'
  images?: {
    image?: MediaType | string
    id?: string
  }[]
  description?: string
}

interface CtaBlockFields {
  blockType: 'cta'
  text: string
  link: string
}

interface YoutubeIframeBlockFields {
  blockType: 'youtube-iframe'
  videoId: string
  title?: string
  description?: string
}

interface CanvaIframeBlockFields {
  blockType: 'canva-iframe'
  embedUrl: string
  title?: string
  description?: string
}

interface HeaderBlockFields {
  blockType: 'header'
  backgroundImage: MediaType | string
}

interface PostType {
  id?: string
  title?: string
  slug?: string
  description?: string | null
  featureImage?: MediaType | string
  tags?: { slug: string; name?: string }
  author?: { name?: string } | string
}

interface BlogShowcaseBlockFields {
  blockType: 'blog-showcase'
  posts?: PostType | string
}

interface ContributorType {
  id?: string
  name?: string
  avatar?: MediaType | string
}

interface BookThemeType {
  id?: string
  name?: string
  icon?: MediaType | string
}

interface BookType {
  id?: string
  title?: string
  slug?: string
  synopsis?: string
  isbn?: string | null
  cover?: MediaType | string
  authors?: ContributorType[] | string[]
  illustrators?: ContributorType[] | string[]
  themes?: BookThemeType[] | string[]
}

interface BookShowcaseBlockFields {
  blockType: 'book-showcase'
  books?: BookType | string
}

interface FeatureGridBlockFields {
  blockType: 'feature-grid'
  title: string
  subtitle?: string
  subtitleHighlight?: string
  description?: string
  items?: {
    icon?: MediaType | string
    title: string
    description: string
    id?: string
  }[]
}

// Generic block node type for block converters
interface BlockNode<T> {
  fields: T
}

/* =============================================================================
   Lexical Converters Factory
   ============================================================================= */

const createConverters: JSXConvertersFunction = ({ defaultConverters }) => {
  return {
    ...defaultConverters,

    /* ---------------------------------------------------------------------------
     Built-in Text Nodes
     --------------------------------------------------------------------------- */

    // Paragraph
    paragraph: ({ node, nodesToJSX }) => (
      <p className={cn(typographyVariants({ variant: 'body-lg' }), 'ui:mb-4 ui:last:mb-0 ui:text-neutral-black')}>
        {nodesToJSX({ nodes: node.children })}
      </p>
    ),

    // Headings
    heading: ({ node, nodesToJSX }) => {
      const Tag = node.tag
      const variantMap = {
        h1: 'headline-lg',
        h2: 'headline-md',
        h3: 'headline-sm',
        h4: 'title-lg',
        h5: 'title-md',
        h6: 'title-sm',
      } as const
      const spacingMap = {
        h1: 'ui:mb-6 ui:mt-8',
        h2: 'ui:mb-5 ui:mt-7',
        h3: 'ui:mb-4 ui:mt-6',
        h4: 'ui:mb-3 ui:mt-5',
        h5: 'ui:mb-3 ui:mt-4',
        h6: 'ui:mb-2 ui:mt-4',
      }
      return (
        <Tag
          className={cn(
            typographyVariants({ variant: variantMap[Tag] }),
            spacingMap[Tag],
            'ui:text-neutral-black ui:first:mt-0',
          )}
        >
          {nodesToJSX({ nodes: node.children })}
        </Tag>
      )
    },

    // Text formatting
    text: ({ node }) => {
      let text: React.ReactNode = node.text

      // Apply formatting based on node.format (bitmask)
      // Lexical uses a bitmask: 1=bold, 2=italic, 4=strikethrough, 8=underline, 16=code, 32=subscript, 64=superscript
      if (node.format & 1) {
        // Bold
        text = <strong className='ui:font-bold'>{text}</strong>
      }
      if (node.format & 2) {
        // Italic
        text = <em className='ui:italic'>{text}</em>
      }
      if (node.format & 4) {
        // Strikethrough
        text = <s className='ui:line-through ui:opacity-70'>{text}</s>
      }
      if (node.format & 8) {
        // Underline
        text = <u className='ui:underline ui:underline-offset-2'>{text}</u>
      }
      if (node.format & 16) {
        // Inline Code
        text = (
          <code className='ui:rounded-md ui:px-1.5 ui:py-0.5 ui:font-mono ui:text-sm ui:bg-grey-10 ui:text-navy-100'>
            {text}
          </code>
        )
      }
      if (node.format & 32) {
        // Subscript
        text = <sub className='ui:text-xs'>{text}</sub>
      }
      if (node.format & 64) {
        // Superscript
        text = <sup className='ui:text-xs'>{text}</sup>
      }

      return <>{text}</>
    },

    /* ---------------------------------------------------------------------------
     Links
     --------------------------------------------------------------------------- */

    link: ({ node, nodesToJSX }) => {
      const fields = node.fields as { url?: string } | undefined
      const url = fields?.url ?? '#'
      const isExternal = url.startsWith('http') || url.startsWith('//')

      if (isExternal) {
        return (
          <a
            href={url}
            target='_blank'
            rel='noopener noreferrer'
            className='ui:underline ui:underline-offset-2 ui:transition-colors ui:text-blue-100 ui:hover:text-blue-70'
          >
            {nodesToJSX({ nodes: node.children })}
          </a>
        )
      }

      return (
        <a
          href={url}
          className='ui:underline ui:underline-offset-2 ui:transition-colors ui:text-blue-100 ui:hover:text-blue-70'
        >
          {nodesToJSX({ nodes: node.children })}
        </a>
      )
    },

    autolink: ({ node, nodesToJSX }) => {
      const fields = node.fields as { url?: string } | undefined
      return (
        <a
          href={fields?.url ?? '#'}
          target='_blank'
          rel='noopener noreferrer'
          className='ui:underline ui:underline-offset-2 ui:transition-colors ui:text-blue-100 ui:hover:text-blue-70'
        >
          {nodesToJSX({ nodes: node.children })}
        </a>
      )
    },

    /* ---------------------------------------------------------------------------
     Lists
     --------------------------------------------------------------------------- */

    list: ({ node, nodesToJSX }) => {
      const isOrdered = node.listType === 'number'

      if (isOrdered) {
        return (
          <ol
            className={cn(
              typographyVariants({ variant: 'body-lg' }),
              'ui:mb-4 ui:ml-6 ui:list-decimal ui:space-y-2 ui:text-neutral-black',
            )}
          >
            {nodesToJSX({ nodes: node.children })}
          </ol>
        )
      }

      return (
        <ul
          className={cn(
            typographyVariants({ variant: 'body-lg' }),
            'ui:mb-4 ui:ml-6 ui:list-disc ui:space-y-2 ui:text-neutral-black',
          )}
        >
          {nodesToJSX({ nodes: node.children })}
        </ul>
      )
    },

    listitem: ({ node, nodesToJSX }) => {
      // Check if this is a checklist item
      if (node.checked !== undefined) {
        return (
          <li className='ui:flex ui:items-start ui:gap-2 ui:text-base'>
            <input
              type='checkbox'
              checked={node.checked}
              readOnly
              className='ui:focus:ring-blue-50 ui:mt-1.5 ui:h-4 ui:w-4 ui:rounded ui:border-grey-50 ui:text-blue-100'
            />
            <span className={cn(node.checked && 'ui:text-grey-100 ui:line-through')}>
              {nodesToJSX({ nodes: node.children })}
            </span>
          </li>
        )
      }

      return <li className='ui:leading-relaxed'>{nodesToJSX({ nodes: node.children })}</li>
    },

    /* ---------------------------------------------------------------------------
     Blockquote
     --------------------------------------------------------------------------- */

    quote: ({ node, nodesToJSX }) => {
      return (
        <blockquote
          className={cn(
            typographyVariants({ variant: 'label-lg' }),
            'ui:my-6 ui:border-l-4 ui:py-4 ui:pr-4 ui:pl-6 ui:border-blue-100 ui:bg-blue-10 ui:text-navy-100 ui:italic',
          )}
        >
          {nodesToJSX({ nodes: node.children })}
        </blockquote>
      )
    },

    /* ---------------------------------------------------------------------------
     Horizontal Rule
     --------------------------------------------------------------------------- */

    horizontalrule: () => <hr className='ui:my-8 ui:border-t ui:border-outline' />,

    /* ---------------------------------------------------------------------------
     Upload (Images)
     --------------------------------------------------------------------------- */

    upload: ({ node }) => {
      const media = node.value as MediaType | undefined

      if (!media?.url) {
        return null
      }

      const isImage = media.mimeType?.startsWith('image/')

      if (!isImage) {
        const formatFileSize = (bytes?: number | null) => {
          if (!bytes) return ''
          if (bytes < 1024) return `${bytes} B`
          if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
          return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
        }

        const filename = media.filename ?? media.url.split('/').pop() ?? 'Download'
        const filesize = formatFileSize(media.filesize)

        return (
          <div className='ui:my-6'>
            <a
              href={media.url}
              download
              target='_blank'
              rel='noopener noreferrer'
              className='ui:flex ui:items-center ui:gap-4 ui:rounded-lg ui:border ui:p-4 ui:transition-colors ui:border-grey-30 ui:hover:border-grey-50 ui:hover:bg-grey-10'
            >
              <div className='ui:bg-blue-100/10 ui:text-blue-100 ui:flex ui:h-10 ui:w-10 ui:shrink-0 ui:items-center ui:justify-center ui:rounded-full'>
                <Download className='ui:h-5 ui:w-5' />
              </div>
              <div className='ui:min-w-0 ui:flex-1'>
                <p className='ui:truncate ui:font-medium ui:text-grey-90'>{media.alt ?? filename}</p>
                <p className='ui:text-sm ui:text-grey-60'>
                  {filename}
                  {filesize && ` · ${filesize}`}
                </p>
              </div>
            </a>
          </div>
        )
      }

      return (
        <figure className='ui:my-6'>
          <div className='ui:relative ui:w-full ui:overflow-hidden ui:rounded-lg ui:flex ui:justify-center'>
            <Zoom>
              <img
                src={media.url}
                alt={media.alt ?? ''}
                className='ui:h-auto ui:w-full ui:max-w-125 ui:object-contain'
                loading='lazy'
                data-zoom
              />
            </Zoom>
          </div>
          {media.alt && (
            <figcaption className='ui:mt-2 ui:text-center ui:text-sm ui:text-grey-100'>{media.alt}</figcaption>
          )}
        </figure>
      )
    },

    /* ---------------------------------------------------------------------------
     Custom Blocks
     --------------------------------------------------------------------------- */

    blocks: {
      alert: ({ node }: { node: BlockNode<AlertBlockFields> }) => {
        return <AlertBlock fields={node.fields} />
      },

      'callout-box': ({ node }: { node: BlockNode<CalloutBoxBlockFields> }) => {
        const fields = node.fields
        return <CalloutBox heading={fields.heading} ctaText={fields.ctaText} link={fields.link} />
      },

      quote: ({ node }: { node: BlockNode<QuoteBlockFields> }) => {
        return <QuoteBlock fields={node.fields} />
      },

      gallery: ({ node }: { node: BlockNode<GalleryBlockFields> }) => {
        return <GalleryBlock fields={node.fields} />
      },

      cta: ({ node }: { node: BlockNode<CtaBlockFields> }) => {
        return <CtaBlock fields={node.fields} />
      },

      'youtube-iframe': ({ node }: { node: BlockNode<YoutubeIframeBlockFields> }) => {
        return <YoutubeIframeBlock fields={node.fields} />
      },

      'canva-iframe': ({ node }: { node: BlockNode<CanvaIframeBlockFields> }) => {
        return <CanvaIframeBlock fields={node.fields} />
      },

      header: ({ node }: { node: BlockNode<HeaderBlockFields> }) => {
        return <HeaderBlock fields={node.fields} />
      },

      'blog-showcase': ({ node }: { node: BlockNode<BlogShowcaseBlockFields> }) => {
        return <BlogShowcaseBlock fields={node.fields} />
      },

      'book-showcase': ({ node }: { node: BlockNode<BookShowcaseBlockFields> }) => {
        return <BookShowcaseBlock fields={node.fields} />
      },

      'feature-grid': ({ node }: { node: BlockNode<FeatureGridBlockFields> }) => {
        return <FeatureGridBlock fields={node.fields} />
      },
    },

    /* ---------------------------------------------------------------------------
     Line Break
     --------------------------------------------------------------------------- */

    linebreak: () => <br />,

    /* ---------------------------------------------------------------------------
     Tab
     --------------------------------------------------------------------------- */

    tab: () => <span className='ui:inline-block ui:w-8' />,

    unknownNodeType: ({ node }: { node: { type?: string } }) => {
      console.warn('Unknown node type encountered:', node.type, node)
      return null
    },
  }
}

/* =============================================================================
   RichText Component Export
   ============================================================================= */

interface RichTextProps {
  data: SerializedEditorState
  className?: string
}

export function RichText({ data, className }: RichTextProps) {
  return (
    <div className={cn('ui:prose ui:prose-lg ui:max-w-none', className)}>
      <PayloadRichText data={data} converters={createConverters} />
    </div>
  )
}

export { defaultJSXConverters }
